export const worldOrder = ["space", "city", "wildlife", "farm", "zombie", "magic"];

export const bossQuestions = {
  space: {
    type: "math",
    context: "The reactor control panel unlocks only if you solve the equation correctly.",
    q: "Solve 3(2x - 5) = 39. What is x?",
    options: ["7", "8", "9", "10"],
    answer: "9",
    explain: "Divide by 3: 2x - 5 = 13. Then 2x = 18, so x = 9."
  },
  wildlife: {
    type: "math",
    context: "You must estimate real distance to guide rescue teams across the reserve.",
    q: "Map scale is 1:25,000. Two points are 7.2 cm apart on the map. What is the real distance?",
    options: ["1.2 km", "1.8 km", "2.5 km", "3.6 km"],
    answer: "1.8 km",
    explain: "7.2 × 25,000 = 180,000 cm = 1,800 m = 1.8 km."
  },
  farm: {
    type: "math",
    context: "The final harvest plan requires exact field area calculations.",
    q: "A field is 48 m by 35 m. If 3/5 of it is planted with corn, what area is corn?",
    options: ["960 m²", "1008 m²", "1120 m²", "1260 m²"],
    answer: "1008 m²",
    explain: "Total area = 48 × 35 = 1680 m². Corn area = 1680 × 3/5 = 1008 m²."
  },
  city: {
    type: "math",
    context: "The city core loses energy by a fixed percentage each mission cycle.",
    q: "A battery starts at 100%. It loses 12% of full capacity each mission. After 4 missions, how much remains?",
    options: ["48%", "52%", "56%", "60%"],
    answer: "52%",
    explain: "Loss is 4 × 12% = 48%, so remaining energy is 100% - 48% = 52%."
  },
  magic: {
    type: "math",
    context: "The portal opens only when two rune groups are activated together.",
    q: "2/3 of 45 runes and 40% of 30 runes must glow. How many runes glow in total?",
    options: ["36", "40", "42", "45"],
    answer: "42",
    explain: "2/3 of 45 = 30, and 40% of 30 = 12. Total = 30 + 12 = 42."
  },
  zombie: [
    {
      type: "math",
      context: "Before departure, you must calculate transport capacity correctly.",
      q: "Each bus carries 36 people. There are 5 survivor groups of 8 people each. Minimum number of buses needed?",
      options: ["1", "2", "3", "4"],
      answer: "2",
      explain: "Total people = 5 × 8 = 40. One bus is not enough, so you need 2 buses."
    },
    {
      type: "science",
      context: "Your purifier removes 90% of bacteria in one cycle.",
      q: "After purification, 1,000 bacteria remain. About how many bacteria were there before the cycle?",
      options: ["9,000", "10,000", "11,000", "12,000"],
      answer: "10,000",
      explain: "If 90% is removed, 10% remains. 1,000 is 10%, so original amount is 10,000."
    }
  ]
};

export const questionsByWorld = {
  space: {
    easy: [
      {
        type: "math",
        context: "Your spaceship collects small energy crystals.",
        q: "You collect 3 crystals. Each has 2 energy points. Total energy?",
        options: ["6", "5", "4", "8"],
        answer: "6",
        explain: "3 × 2 = 6."
      },
      {
        type: "logic",
        context: "You find three doors inside the alien cave.",
        q: "The clue says: 'The safe door is not red and not green.' Which door is safe?",
        options: ["Red", "Blue", "Green", "All of them"],
        answer: "Blue",
        explain: "If it is not red and not green, it must be blue."
      },
      {
        type: "science",
        context: "Your scanner studies the planet sky.",
        q: "Which of these helps astronauts breathe in space?",
        options: ["Helmet", "Boots", "Backpack", "Gloves"],
        answer: "Helmet",
        explain: "The helmet is part of the life-support system."
      }
    ],
    medium: [
      {
        type: "pattern",
        context: "Alien lights blink in a pattern near the ruins.",
        q: "What comes next? 2, 4, 8, 16, ?",
        options: ["18", "24", "32", "20"],
        answer: "32",
        explain: "The number doubles each time."
      },
      {
        type: "comparison",
        context: "You can choose one energy source for your scanner.",
        q: "Which gives more total energy: 4 crystals worth 5 each, or 3 crystals worth 7 each?",
        options: ["4 crystals", "3 crystals", "They are the same", "Not enough information"],
        answer: "3 crystals",
        explain: "4×5=20, while 3×7=21. So 3 crystals gives more energy."
      },
      {
        type: "science",
        context: "Alien plants glow only at night.",
        q: "What does that suggest?",
        options: [
          "They may react to darkness",
          "They are made of metal",
          "They are animals",
          "They can fly"
        ],
        answer: "They may react to darkness",
        explain: "Glowing only at night suggests they respond to darkness."
      }
    ],
    hard: [
      {
        type: "math",
        context: "Your engine needs power cells to restart.",
        q: "You have 5 cells worth 8 energy each. Total energy?",
        options: ["40", "36", "48", "30"],
        answer: "40",
        explain: "5 × 8 = 40."
      },
      {
        type: "logic",
        context: "The reactor has four switches. Only one opens the gate.",
        q: "A clue says: 'Switch A does not work. Switch D works only if A works.' Which switch is impossible?",
        options: ["A", "B", "C", "D"],
        answer: "D",
        explain: "If A does not work, then D cannot work either."
      },
      {
        type: "decision",
        context: "You can carry only one tool into the ruins.",
        q: "Which tool is the best choice if you want to study symbols on the wall?",
        options: ["Scanner", "Food pack", "Rope", "Blanket"],
        answer: "Scanner",
        explain: "A scanner is the most useful tool for studying symbols."
      }
    ]
  },

  wildlife: {
    easy: [
      {
        type: "math",
        context: "You see several deer near the river.",
        q: "There are 4 groups of deer. Each group has 3 deer. How many deer are there?",
        options: ["12", "10", "9", "15"],
        answer: "12",
        explain: "4 × 3 = 12 deer."
      },
      {
        type: "science",
        context: "You observe animals near the lake.",
        q: "Which animal is a mammal?",
        options: ["Snake", "Frog", "Whale", "Lizard"],
        answer: "Whale",
        explain: "Whales are mammals."
      },
      {
        type: "observation",
        context: "You use binoculars from the hill.",
        q: "What is the best reason to use binoculars?",
        options: [
          "To see far-away animals clearly",
          "To make sounds louder",
          "To smell plants",
          "To keep warm"
        ],
        answer: "To see far-away animals clearly",
        explain: "Binoculars help you see things far away."
      }
    ],
    medium: [
      {
        type: "math",
        context: "You observe monkeys jumping between trees.",
        q: "Each tree has 5 monkeys. There are 4 trees. How many monkeys?",
        options: ["20", "18", "16", "22"],
        answer: "20",
        explain: "4 × 5 = 20 monkeys."
      },
      {
        type: "pattern",
        context: "Animal movement follows a pattern.",
        q: "What comes next? 3, 6, 12, 24, ?",
        options: ["30", "36", "48", "60"],
        answer: "48",
        explain: "The number doubles each time."
      },
      {
        type: "decision",
        context: "You want to protect the animals without disturbing them.",
        q: "Which is the best choice?",
        options: [
          "Watch quietly from far away",
          "Run toward them quickly",
          "Throw food near them",
          "Shout to get attention"
        ],
        answer: "Watch quietly from far away",
        explain: "That lets you observe without disturbing them."
      }
    ],
    hard: [
      {
        type: "math",
        context: "Animals gather near the lake in groups.",
        q: "Each group has 7 animals. There are 6 groups. How many animals?",
        options: ["42", "36", "48", "40"],
        answer: "42",
        explain: "6 × 7 = 42 animals."
      },
      {
        type: "comparison",
        context: "You compare two herds from different parts of the reserve.",
        q: "Which is greater: 6 groups of 5 animals, or 4 groups of 7 animals?",
        options: ["6 groups of 5", "4 groups of 7", "They are equal", "Not enough information"],
        answer: "6 groups of 5",
        explain: "6×5=30 while 4×7=28."
      },
      {
        type: "logic",
        context: "You are following tracks through the forest.",
        q: "If fresh tracks always lead to water, and these tracks are fresh, what is most likely nearby?",
        options: ["A lake", "A cave", "A mountain", "A robot"],
        answer: "A lake",
        explain: "If fresh tracks always lead to water, water is the best answer."
      }
    ]
  },

  farm: {
    easy: [
      {
        type: "math",
        context: "You feed the cows in the field.",
        q: "There are 4 cows. Each cow eats 3 apples. How many apples are needed?",
        options: ["12", "10", "9", "15"],
        answer: "12",
        explain: "4 × 3 = 12 apples."
      },
      {
        type: "math",
        context: "You collect eggs from chickens.",
        q: "Each chicken lays 2 eggs. You have 5 chickens. How many eggs?",
        options: ["10", "8", "12", "9"],
        answer: "10",
        explain: "5 × 2 = 10 eggs."
      },
      {
        type: "decision",
        context: "The animals are hungry and waiting.",
        q: "What should you do first?",
        options: [
          "Feed the hungry animals",
          "Take a nap",
          "Hide the food",
          "Leave the farm"
        ],
        answer: "Feed the hungry animals",
        explain: "Feeding the animals is the most responsible first step."
      }
    ],
    medium: [
      {
        type: "math",
        context: "You harvest apples from the orchard.",
        q: "Each tree produces 6 apples. There are 5 trees. How many apples?",
        options: ["30", "25", "28", "32"],
        answer: "30",
        explain: "5 × 6 = 30 apples."
      },
      {
        type: "comparison",
        context: "You compare two farm deliveries.",
        q: "Which is more: 7 sheep eating 4 carrots each, or 6 cows eating 5 apples each?",
        options: ["Sheep", "Cows", "They are equal", "Not enough information"],
        answer: "Cows",
        explain: "7×4=28 while 6×5=30."
      },
      {
        type: "science",
        context: "You are helping crops grow.",
        q: "Which of these helps plants grow?",
        options: ["Sunlight", "Plastic", "Sandwiches", "Shoes"],
        answer: "Sunlight",
        explain: "Plants need sunlight to grow."
      }
    ],
    hard: [
      {
        type: "math",
        context: "You store harvested crops in baskets.",
        q: "Each basket holds 8 apples. You fill 6 baskets. Total apples?",
        options: ["48", "40", "56", "44"],
        answer: "48",
        explain: "6 × 8 = 48 apples."
      },
      {
        type: "logic",
        context: "The farmer says the red barn stores tools, not food. The blue barn stores food. You need food.",
        q: "Which barn should you go to?",
        options: ["Red barn", "Blue barn", "Both barns", "Neither barn"],
        answer: "Blue barn",
        explain: "The clue says the blue barn stores food."
      },
      {
        type: "decision",
        context: "You can only do one thing before sunset.",
        q: "Which task should come first if the animals are still hungry?",
        options: [
          "Feed the animals",
          "Paint the fence",
          "Sort old boxes",
          "Count clouds"
        ],
        answer: "Feed the animals",
        explain: "The hungry animals need urgent care first."
      }
    ]
  },

  city: {
    easy: [
      {
        type: "math",
        context: "Robots are produced in the factory.",
        q: "Each machine builds 3 robots. There are 4 machines. How many robots?",
        options: ["12", "10", "9", "15"],
        answer: "12",
        explain: "4 × 3 = 12 robots."
      },
      {
        type: "math",
        context: "Hover taxis wait at stations.",
        q: "Each station has 5 taxis. There are 2 stations. Total taxis?",
        options: ["10", "12", "8", "14"],
        answer: "10",
        explain: "5 × 2 = 10 taxis."
      },
      {
        type: "logic",
        context: "A robot scientist gives you a clue.",
        q: "If Robot A is charging and Robot B is not, which robot has power right now?",
        options: ["Robot A", "Robot B", "Both", "Neither"],
        answer: "Robot A",
        explain: "Charging means Robot A has power."
      }
    ],
    medium: [
      {
        type: "math",
        context: "Delivery robots transport packages.",
        q: "Each robot carries 4 boxes. There are 6 robots. Total boxes?",
        options: ["24", "20", "18", "28"],
        answer: "24",
        explain: "6 × 4 = 24 boxes."
      },
      {
        type: "science",
        context: "The city runs on energy cells.",
        q: "What is the best reason to save energy in a city?",
        options: [
          "To make power last longer",
          "To make roads smaller",
          "To make robots heavier",
          "To stop math"
        ],
        answer: "To make power last longer",
        explain: "Saving energy helps resources last longer."
      },
      {
        type: "pattern",
        context: "The city lights blink in a repeating pattern.",
        q: "What comes next? Red, Blue, Red, Blue, ?",
        options: ["Red", "Green", "Yellow", "White"],
        answer: "Red",
        explain: "The pattern repeats red, blue."
      }
    ],
    hard: [
      {
        type: "math",
        context: "The robot factory produces drones.",
        q: "Each line builds 8 drones per hour. There are 5 lines. Total drones?",
        options: ["40", "36", "48", "32"],
        answer: "40",
        explain: "5 × 8 = 40 drones."
      },
      {
        type: "logic",
        context: "The city control core has four switches.",
        q: "A clue says: 'Switch A is broken. Switch D works only if A works.' Which switch is impossible?",
        options: ["A", "B", "C", "D"],
        answer: "D",
        explain: "If A is broken, D cannot work."
      },
      {
        type: "decision",
        context: "You can only bring one tool to fix a robot control panel.",
        q: "Which is the most useful tool?",
        options: ["Scanner", "Blanket", "Apple", "Boot"],
        answer: "Scanner",
        explain: "A scanner is best for checking a control panel."
      }
    ]
  },

  magic: {
    easy: [
      {
        type: "math",
        context: "You collect magic crystals for a spell.",
        q: "You collect 3 crystals. Each crystal has 2 magic points. Total power?",
        options: ["6", "5", "4", "8"],
        answer: "6",
        explain: "3 × 2 = 6 magic points."
      },
      {
        type: "math",
        context: "You mix potion ingredients.",
        q: "Each potion needs 4 herbs. You make 2 potions. How many herbs?",
        options: ["8", "6", "10", "12"],
        answer: "8",
        explain: "4 × 2 = 8 herbs."
      },
      {
        type: "logic",
        context: "A wizard gives you a clue.",
        q: "If the silver key opens the library and you need the library, which key should you take?",
        options: ["Silver key", "Gold key", "Blue key", "No key"],
        answer: "Silver key",
        explain: "You need the key that opens the library."
      }
    ],
    medium: [
      {
        type: "math",
        context: "Magic scrolls contain spell symbols.",
        q: "Each scroll has 5 symbols. You read 6 scrolls. Total symbols?",
        options: ["30", "25", "28", "32"],
        answer: "30",
        explain: "5 × 6 = 30 symbols."
      },
      {
        type: "pattern",
        context: "Spell lights flash in a magical sequence.",
        q: "What comes next? 1, 2, 4, 8, ?",
        options: ["12", "14", "16", "18"],
        answer: "16",
        explain: "The number doubles each time."
      },
      {
        type: "science",
        context: "A potion changes color when heated.",
        q: "What most likely caused the color change?",
        options: [
          "Heat changed the potion",
          "The table changed it",
          "The book changed it",
          "Nothing changed it"
        ],
        answer: "Heat changed the potion",
        explain: "The clue says the potion changed when heated."
      }
    ],
    hard: [
      {
        type: "math",
        context: "Magic crystals power the portal.",
        q: "Each crystal gives 8 energy. You have 6 crystals. Total energy?",
        options: ["48", "40", "56", "44"],
        answer: "48",
        explain: "6 × 8 = 48 energy."
      },
      {
        type: "logic",
        context: "The portal opens only if the moon rune is active. The moon rune is not active.",
        q: "What can you conclude?",
        options: [
          "The portal cannot open yet",
          "The portal is already open",
          "The portal will open by itself",
          "The portal is broken forever"
        ],
        answer: "The portal cannot open yet",
        explain: "If the needed rune is not active, the portal cannot open yet."
      },
      {
        type: "decision",
        context: "You can prepare only one item before entering the portal.",
        q: "Which is the smartest choice?",
        options: ["Spell book", "Pillow", "Empty cup", "Shoe"],
        answer: "Spell book",
        explain: "A spell book is the most useful item for a magical portal."
      }
    ]
  },

  zombie: {
    easy: [
      {
        type: "math",
        context: "You collect food supplies for survivors.",
        q: "You find 4 boxes. Each box has 3 food packs. Total packs?",
        options: ["12", "10", "9", "15"],
        answer: "12",
        explain: "4 × 3 = 12 packs."
      },
      {
        type: "math",
        context: "You count survivors in the shelter.",
        q: "Each room has 2 survivors. There are 6 rooms. Total survivors?",
        options: ["12", "10", "14", "8"],
        answer: "12",
        explain: "6 × 2 = 12 survivors."
      },
      {
        type: "decision",
        context: "The shelter needs help quickly.",
        q: "What is the best first step?",
        options: [
          "Count supplies and people",
          "Turn off all lights and sleep",
          "Hide the food",
          "Break the door"
        ],
        answer: "Count supplies and people",
        explain: "Good survival planning starts with knowing your resources."
      }
    ],
    medium: [
      {
        type: "math",
        context: "You see zombie groups in the street.",
        q: "There are 5 groups. Each group has 4 zombies. Total zombies?",
        options: ["20", "18", "16", "22"],
        answer: "20",
        explain: "5 × 4 = 20 zombies."
      },
      {
        type: "logic",
        context: "The map says the safe road is not blocked and not noisy.",
        q: "Which road is safest?",
        options: ["Quiet open road", "Blocked road", "Noisy road", "Broken road"],
        answer: "Quiet open road",
        explain: "The safe road must be open and quiet."
      },
      {
        type: "comparison",
        context: "You compare two supply options.",
        q: "Which gives more food: 4 boxes with 5 packs each, or 3 boxes with 7 packs each?",
        options: ["4 boxes", "3 boxes", "They are equal", "Not enough information"],
        answer: "3 boxes",
        explain: "4×5=20, while 3×7=21."
      }
    ],
    hard: [
      {
        type: "math",
        context: "You load supplies onto the escape bus.",
        q: "Each box holds 8 food packs. There are 6 boxes. Total packs?",
        options: ["48", "40", "56", "44"],
        answer: "48",
        explain: "6 × 8 = 48 packs."
      },
      {
        type: "logic",
        context: "The escape gate opens only if the power is on and the lock code is correct. The power is off.",
        q: "What is true right now?",
        options: [
          "The gate cannot open yet",
          "The gate is already open",
          "The code does not matter ever",
          "The zombies opened the gate"
        ],
        answer: "The gate cannot open yet",
        explain: "If the power is off, the gate cannot open yet."
      },
      {
        type: "decision",
        context: "You can carry only one thing to the bus.",
        q: "Which is the best choice?",
        options: ["Food", "Toy", "Rock", "Poster"],
        answer: "Food",
        explain: "Food is the most useful choice for survival."
      }
    ]
  }
};

export const worlds = {
  space: {
    start: {
      story:
        "Your spaceship crash-lands on a silent alien planet. A tiny glowing guide named Lumi appears and tells you that the Wonder Bridge is breaking apart. Your engine is dead, and the first clue is hidden somewhere nearby.",
      choices: [
        { text: "Explore a glowing cave nearby", next: "cave", tag: "exploration" },
        { text: "Walk toward a strange alien forest", next: "forest", tag: "curiosity" }
      ]
    },
    cave: {
      story:
        "Inside the cave, floating crystals pulse like slow heartbeats. Lumi says this light feels older than your ship — like part of a forgotten signal system.",
      choices: [
        { text: "Collect some crystals", next: "question", tag: "risk" },
        { text: "Study strange symbols on the cave wall", next: "symbols", tag: "analysis" }
      ]
    },
    forest: {
      story:
        "The alien forest is alive with moving vines and soft blue leaves. Some of the plants seem to lean in one direction, as if they are guiding you toward something important.",
      choices: [
        { text: "Collect glowing plants for research", next: "question", tag: "exploration" },
        { text: "Follow a trail of moving plants", next: "ruins", tag: "curiosity" }
      ]
    },
    symbols: {
      story:
        "The cave symbols describe an ancient energy network once used to connect distant worlds. A broken map appears in the pattern, pointing toward old ruins beyond the ridge.",
      choices: [
        { text: "Try to decode the symbols", next: "question", tag: "analysis" },
        { text: "Follow the map shown in the symbols", next: "ruins", tag: "planning" }
      ]
    },
    ruins: {
      story:
        "You reach ancient alien ruins built around a dormant signal tower. Mirror panels, cracked pathways, and a faint hum suggest the place could still wake up.",
      choices: [
        { text: "Examine the device carefully", next: "question", tag: "analysis" },
        { text: "Search the ruins for more clues", next: "reactor", tag: "planning" }
      ]
    },
    reactor: {
      story:
        "Behind the ruins, you discover an ancient energy reactor. If it restarts, your ship may fly again — and Lumi believes it will reveal where the next missing fragment has gone.",
      choices: [
        { text: "Try to activate the reactor", next: "boss", tag: "risk" }
      ]
    }
  },

  city: {
    start: {
      story:
        "When the reactor comes online, a hologram points you to Neon Harbor, a futuristic city losing power one block at a time. Lumi says the second fragment may be hidden where the city thinks for itself.",
      choices: [
        { text: "Visit the robot factory", next: "factory", tag: "logic" },
        { text: "Explore the hover train station", next: "station", tag: "exploration" }
      ]
    },
    factory: {
      story:
        "Inside the robot factory, machines are still building, but slower than they should. Some assembly lines have stopped completely, while others keep repeating the same broken command.",
      choices: [
        { text: "Observe robot production", next: "question", tag: "analysis" },
        { text: "Inspect the assembly lines", next: "control", tag: "planning" }
      ]
    },
    station: {
      story:
        "At the hover train station, service bots keep rerouting passengers unevenly. Busy districts stay bright, while family neighborhoods flicker and wait in the dark.",
      choices: [
        { text: "Count the trains arriving", next: "question", tag: "observation" },
        { text: "Follow a robot delivery route", next: "control", tag: "exploration" }
      ]
    },
    control: {
      story:
        "The city control center shows something strange: the power problem is not only inside the city. Wildlife routes, weather signals, and traffic systems are all drifting together, as if one hidden force is pulling everything off balance.",
      choices: [
        { text: "Analyze the robot network", next: "question", tag: "logic" },
        { text: "Check the energy supply system", next: "core", tag: "analysis" }
      ]
    },
    core: {
      story:
        "At the center of the city lies the AI energy core. Lumi recognizes the same pulse you saw on the alien planet. If you stabilize it, the city may reveal where the third fragment has gone.",
      choices: [
        { text: "Stabilize the energy core", next: "boss", tag: "planning" }
      ]
    }
  },

  wildlife: {
    start: {
      story:
        "The city core projects a map of a wildlife reserve. Animal paths from every direction are bending toward one quiet lake. Lumi says nature may be feeling the broken bridge before anyone else.",
      choices: [
        { text: "Follow fresh animal footprints", next: "tracks", tag: "exploration" },
        { text: "Climb a hill to observe animals from above", next: "hill", tag: "analysis" }
      ]
    },
    tracks: {
      story:
        "You follow fresh tracks to a riverbank where deer stand alert and silent. They are not grazing. They are watching the woods, as if waiting for a signal that has not come.",
      choices: [
        { text: "Count the deer carefully", next: "question", tag: "observation" },
        { text: "Continue following the tracks deeper into the forest", next: "forest", tag: "exploration" }
      ]
    },
    hill: {
      story:
        "From the hill, you can see birds, deer, and smaller animals all moving in unusual patterns. They are not fleeing randomly. Somehow, they seem to be searching for the same place.",
      choices: [
        { text: "Use binoculars to count them", next: "question", tag: "analysis" },
        { text: "Walk toward a herd of animals", next: "forest", tag: "risk" }
      ]
    },
    forest: {
      story:
        "Inside the forest, monkeys leap from branch to branch above you while the lower paths stay strangely empty. Even the wind feels careful here, like the whole reserve is holding its breath.",
      choices: [
        { text: "Observe how they move in groups", next: "question", tag: "observation" },
        { text: "Search the forest for other animals", next: "lake", tag: "curiosity" }
      ]
    },
    lake: {
      story:
        "You arrive at a peaceful lake where animals from different habitats gather together without fear. Under an old tree near the water, Lumi senses a sleeping fragment hidden beneath the roots.",
      choices: [
        { text: "Observe the animals at the lake", next: "boss", tag: "planning" }
      ]
    }
  },

  farm: {
    start: {
      story:
        "Beneath the tree roots, you find a message: 'When the ground forgets how to grow, seek the hands that still care for it.' Lumi leads you to a farm where the soil has grown cold and quiet.",
      choices: [
        { text: "Feed the animals", next: "animals", tag: "care" },
        { text: "Check the apple orchard", next: "orchard", tag: "planning" }
      ]
    },
    animals: {
      story:
        "The cows and sheep are waiting, but something feels off. They are calm, yet restless, as if they can sense a storm that has not arrived yet.",
      choices: [
        { text: "Prepare food for the animals", next: "question", tag: "care" },
        { text: "Count the animals in the field", next: "barn", tag: "observation" }
      ]
    },
    orchard: {
      story:
        "The apple orchard is full, but the leaves look tired and the ground is too dry in some rows and too soft in others. The farm still works, but only because people are working harder than the land itself.",
      choices: [
        { text: "Collect apples", next: "question", tag: "efficiency" },
        { text: "Inspect the orchard rows", next: "barn", tag: "analysis" }
      ]
    },
    barn: {
      story:
        "Inside the barn, you find tools, baskets, and several families who arrived from the city. They speak of a gray shadow spreading through the streets and forcing people to abandon their homes.",
      choices: [
        { text: "Organize the supplies", next: "question", tag: "planning" },
        { text: "Prepare food storage", next: "harvest", tag: "efficiency" }
      ]
    },
    harvest: {
      story:
        "At sunset, the old windmill finally turns again, and a warm pulse moves through the field. The fourth fragment wakes up — but in the distance, a column of gray mist rises above the city.",
      choices: [
        { text: "Finish today's farm work", next: "boss", tag: "planning" }
      ]
    }
  },

  zombie: {
    start: {
      story:
        "You reach the edge of the city and see the gray mist for yourself. The people wandering through it do not seem like monsters. They look lost, as if the shadow has stolen their memory of where they belong.",
      choices: [
        { text: "Search an abandoned house", next: "house", tag: "risk" },
        { text: "Explore a quiet street", next: "street", tag: "observation" }
      ]
    },
    house: {
      story:
        "Inside the house, you find food, blankets, flashlights, and notes from a family that left in a hurry. One line is underlined twice: 'The school is still safe. For now.'",
      choices: [
        { text: "Collect food supplies", next: "question", tag: "planning" },
        { text: "Search the basement", next: "school", tag: "risk" }
      ]
    },
    street: {
      story:
        "The street is almost silent except for distant footsteps in the mist. Shapes drift between the buildings, slow and confused. They are not chasing anyone. They are following something unseen.",
      choices: [
        { text: "Count zombie groups carefully", next: "question", tag: "analysis" },
        { text: "Run toward the school building", next: "school", tag: "risk" }
      ]
    },
    school: {
      story:
        "The school has become a shelter full of frightened families. Some people want to leave immediately. Others want to stay hidden. Lumi discovers that the gray mist behaves less like a virus and more like a spreading emptiness.",
      choices: [
        { text: "Organize the survivors", next: "question", tag: "cooperation" },
        { text: "Search the building for resources", next: "escape", tag: "planning" }
      ]
    },
    escape: {
      story:
        "In the bus yard, you find transport, fuel, and an old school crest marked with the symbol of a magic academy. The fifth fragment is close — and the crest may point to the final world.",
      choices: [
        { text: "Prepare the final escape plan", next: "boss", tag: "planning" }
      ]
    }
  },

  magic: {
    start: {
      story:
        "With the school crest in hand, you arrive at a silent magic academy. Lumi says this is where the Wonder Bridge was once watched over. If the final gate opens, every world may remember how to connect again.",
      choices: [
        { text: "Visit the spell library", next: "library", tag: "curiosity" },
        { text: "Walk to the potion laboratory", next: "lab", tag: "exploration" }
      ]
    },
    library: {
      story:
        "The library glows with old books and memory scrolls. They describe the Wonder Bridge not as a road, but as a living link between worlds, held together by trust, courage, and care.",
      choices: [
        { text: "Study a spell book", next: "question", tag: "analysis" },
        { text: "Search ancient scrolls", next: "tower", tag: "planning" }
      ]
    },
    lab: {
      story:
        "The potion laboratory still bubbles with leftover magic. On one table, you find notes about the gray mist: it does not destroy worlds — it makes them forget one another.",
      choices: [
        { text: "Mix potion ingredients", next: "question", tag: "creativity" },
        { text: "Examine potion shelves", next: "tower", tag: "analysis" }
      ]
    },
    tower: {
      story:
        "At the top of the wizard tower, five fragments begin to glow together. The tower shakes softly, but one place in the portal ring is still empty.",
      choices: [
        { text: "Study the crystal energy", next: "question", tag: "analysis" },
        { text: "Follow the magic energy flow", next: "portal", tag: "curiosity" }
      ]
    },
    portal: {
      story:
        "At the portal, Lumi finally understands the truth: the sixth fragment was never missing. It is awakened by the one who listened, explored, protected others, and chose to reconnect the worlds.",
      choices: [
        { text: "Activate the portal", next: "boss", tag: "risk" }
      ]
    }
  }
};
